import { useState } from 'react';
import { Badge, Button, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { formatConcentration, UncertaintyInput } from './formatConcentration';
import { getImageName, getStatusVariant } from './utils';

interface AnalysisStatusPayload {
    ok: boolean;
    flags?: Record<string, { passed: boolean; message?: string }>;
}

export interface AnalysisCardProps {
    sk: string;
    imageS3Key: string;
    status: string;
    created_at: string;
    numPeaks?: number;
    predictedConcentration?: number | null;
    uncertainty?: UncertaintyInput;
    analysisStatus?: AnalysisStatusPayload | null;
    onReanalyze?: () => Promise<void>;
}

export function AnalysisCard({ sk, imageS3Key, status, created_at, numPeaks, predictedConcentration, uncertainty, analysisStatus, onReanalyze }: AnalysisCardProps) {
    const [isRetrying, setIsRetrying] = useState(false);
    const canRetry = status === 'pending' || status === 'failed';

    const handleRetry = async () => {
        if (!onReanalyze) return;
        setIsRetrying(true);
        try {
            await onReanalyze();
        } finally {
            setIsRetrying(false);
        }
    };

    return (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center">
                    <div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <Badge bg={getStatusVariant(status)}>{status}</Badge>
                            {analysisStatus && (
                                <Badge bg={analysisStatus.ok ? 'success' : 'danger'}>
                                    {analysisStatus.ok ? 'QC Passed' : 'QC Failed'}
                                </Badge>
                            )}
                            <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                {new Date(created_at).toLocaleString()}
                            </span>
                            {typeof numPeaks === 'number' && (
                                <span>{numPeaks} peaks detected</span>
                            )}
                        </div>
                        {(predictedConcentration !== undefined && predictedConcentration !== null) && (
                            <div className="mb-1">
                                <strong>Concentration:</strong>{' '}
                                {formatConcentration(predictedConcentration, uncertainty)}
                            </div>
                        )}
                        <div className="fw-semibold">{getImageName(imageS3Key)}</div>
                        <div className="text-muted" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                            {imageS3Key}
                        </div>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        {canRetry && onReanalyze && (
                            <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={handleRetry}
                                disabled={isRetrying}
                            >
                                {isRetrying ? <Spinner animation="border" size="sm" /> : '🔄 Retry'}
                            </Button>
                        )}
                        <Link to={`/digiinsulin/results/${encodeURIComponent(sk)}`}>View details</Link>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
}
